import {
  INSTALMENT_STATUSES,
  setInstalmentStatus,
} from 'components/AdditionalWorkDialog/utils/instalmentUtils';
import uuidv4 from 'uuid/v4';
import { calculateInvoiceRowValues, roundTo2Decimals } from './calculateValues';

export const ADDITIONAL_WORK_STATUSES = Object.freeze({
  PENDING: 'PENDING',
  ORDERED: 'ORDERED',
  CAN_BE_INVOICED: 'CAN_BE_INVOICED',
  INVOICED: 'INVOICED',
  CANCELLED: 'CANCELLED',
});

/** Statuses whose sale sum counts as liikevaihto ("Tilattu or later"). */
export const ADDITIONAL_WORK_REVENUE_STATUSES = Object.freeze([
  ADDITIONAL_WORK_STATUSES.ORDERED,
  ADDITIONAL_WORK_STATUSES.CAN_BE_INVOICED,
  ADDITIONAL_WORK_STATUSES.INVOICED,
]);

/** A missing or unknown status is treated like PENDING, i.e. not revenue. */
export const isAdditionalWorkRevenue = (additionalWork) =>
  ADDITIONAL_WORK_REVENUE_STATUSES.includes(additionalWork?.status);

export const DEFAULT_ALV_PERCENTAGE = 25.5;

/**
 * ALV percentage for an additional work: if every row shares the same
 * alvPercentage that value is used, otherwise (mixed rows or no rows)
 * falls back to the default 25.5 %.
 */
export const deriveAdditionalWorkAlvPercentage = (additionalWork) => {
  const rows = additionalWork?.rows || [];
  if (rows.length === 0) return DEFAULT_ALV_PERCENTAGE;

  const first = Number(rows[0].alvPercentage);
  if (Number.isNaN(first)) return DEFAULT_ALV_PERCENTAGE;

  const uniform = rows.every((row) => Number(row.alvPercentage) === first);
  return uniform ? first : DEFAULT_ALV_PERCENTAGE;
};

/**
 * Builds a sale-invoice-draft row from one additional-work instalment.
 *
 * Instalment `sum` is GROSS (VAT-inclusive) — instalments are distributed from
 * calculateAdditionalWorkTotals(rows).sum (+ productCredits), so the tax-free
 * unit price is derived by dividing out the ALV. Accepted approximations:
 * productCredits mix into the gross pool, and the net→gross round trip may
 * drift ±0.01 € due to rounding.
 *
 * The additionalWorkId/additionalWorkInstalmentId markers let draft deletion
 * revert the instalment back to APPROVED (see useSaleInvoiceDraft).
 */
export const buildDraftRowFromAdditionalWorkInstalment = (
  additionalWork,
  instalment,
  overheadPercentage = 0
) => {
  const alvPercentage = deriveAdditionalWorkAlvPercentage(additionalWork);
  const unitPrice = roundTo2Decimals(
    (Number(instalment.sum) || 0) / (1 + alvPercentage / 100)
  );

  const { taxFreeSum, taxAmount, sum } = calculateInvoiceRowValues(
    unitPrice,
    1,
    alvPercentage,
    0,
    overheadPercentage
  );

  const instalments = additionalWork?.instalments || [];
  const eraIndex = instalments.findIndex((i) => i.id === instalment.id);
  const eraNumber = eraIndex >= 0 ? eraIndex + 1 : 1;

  const namePrefix = additionalWork?.number
    ? `Osakasmuutos ${additionalWork.number}`
    : 'Osakasmuutos';

  return {
    id: uuidv4(),
    additionalWorkId: additionalWork.id,
    additionalWorkInstalmentId: instalment.id,
    productNumber: '',
    name: `${namePrefix} – ${eraNumber}. erä`,
    description: instalment.description || additionalWork.description || '',
    unit: '',
    unitPrice,
    quantity: 1,
    discountPercentage: 0,
    alvPercentage,
    taxFreeSum,
    taxAmount,
    sum,
  };
};

/**
 * Builds sale-invoice-draft rows from a whole additional work: one draft row
 * per AW row, copying name/description/unit/quantity/price/discount/ALV.
 *
 * The AW's own overheadPercentage is baked into each imported unit price —
 * AW row sums are stored with it applied, but the draft recomputes rows from
 * unitPrice with the DRAFT's overhead, which would otherwise drop it.
 *
 * Every row carries the additionalWorkId marker (without an instalment id) so
 * draft deletion reverts the entity status back to CAN_BE_INVOICED.
 */
export const buildDraftRowsFromAdditionalWork = (
  additionalWork,
  overheadPercentage = 0
) => {
  const awOverhead = Number(additionalWork?.overheadPercentage) || 0;
  const sourceRows = additionalWork?.rows || [];

  const fallbackName = additionalWork.number
    ? `Lisätyö ${additionalWork.number}`
    : additionalWork.description || 'Lisätyö';

  // Zero-row AW: keep one placeholder row so the import is visible,
  // editable and revertable.
  if (sourceRows.length === 0) {
    return [
      {
        id: uuidv4(),
        additionalWorkId: additionalWork.id,
        productNumber: '',
        name: fallbackName,
        description: additionalWork.description || '',
        unit: '',
        unitPrice: 0,
        quantity: 1,
        discountPercentage: 0,
        alvPercentage: DEFAULT_ALV_PERCENTAGE,
        taxFreeSum: 0,
        taxAmount: 0,
        sum: 0,
      },
    ];
  }

  return sourceRows.map((row) => {
    const alvNumeric = Number(row.alvPercentage);
    const alvPercentage = Number.isFinite(alvNumeric)
      ? alvNumeric
      : DEFAULT_ALV_PERCENTAGE;
    const unitPrice = roundTo2Decimals(
      (Number(row.unitPrice) || 0) * (1 + awOverhead / 100)
    );
    const quantity = Number(row.quantity) || 0;
    const discountPercentage = Number(row.discountPercentage) || 0;

    const { taxFreeSum, taxAmount, sum } = calculateInvoiceRowValues(
      unitPrice,
      quantity,
      alvPercentage,
      discountPercentage,
      overheadPercentage
    );

    return {
      id: uuidv4(),
      additionalWorkId: additionalWork.id,
      productNumber: '',
      name: row.name || fallbackName,
      description: row.description || '',
      unit: row.unit || '',
      unitPrice,
      quantity,
      discountPercentage,
      alvPercentage,
      taxFreeSum,
      taxAmount,
      sum,
    };
  });
};

/**
 * Returns a new entity with the given instalments flipped APPROVED → INVOICED
 * (skipping any whose current status is not APPROVED). setInstalmentStatus
 * stamps invoicedAt/updatedAt.
 */
export const markAdditionalWorkInstalmentsInvoiced = (
  additionalWork,
  instalmentIds
) => {
  const ids = new Set(instalmentIds || []);
  const eligible = (additionalWork.instalments || []).filter(
    (i) => ids.has(i.id) && i.status === INSTALMENT_STATUSES.APPROVED
  );
  if (!eligible.length) return additionalWork;

  const instalments = eligible.reduce(
    (acc, i) => setInstalmentStatus(acc, i.id, INSTALMENT_STATUSES.INVOICED),
    additionalWork.instalments
  );
  return { ...additionalWork, instalments };
};

/**
 * Returns a new entity with the given instalments flipped INVOICED → APPROVED
 * (skipping any whose current status is not INVOICED, e.g. already PAID).
 */
export const revertAdditionalWorkInstalmentsToApproved = (
  additionalWork,
  instalmentIds
) => {
  const ids = new Set(instalmentIds || []);
  const eligible = (additionalWork.instalments || []).filter(
    (i) => ids.has(i.id) && i.status === INSTALMENT_STATUSES.INVOICED
  );
  if (!eligible.length) return additionalWork;

  const instalments = eligible.reduce(
    (acc, i) => setInstalmentStatus(acc, i.id, INSTALMENT_STATUSES.APPROVED),
    additionalWork.instalments
  );
  return { ...additionalWork, instalments };
};

/** Entity status → INVOICED; the update thunk stamps invoicedAt. */
export const markAdditionalWorkInvoiced = (additionalWork) => ({
  ...additionalWork,
  status: ADDITIONAL_WORK_STATUSES.INVOICED,
});

/**
 * Entity status INVOICED → CAN_BE_INVOICED (only if currently INVOICED);
 * the update thunk stamps canBeInvoicedAt.
 */
export const revertAdditionalWorkToCanBeInvoiced = (additionalWork) =>
  additionalWork.status === ADDITIONAL_WORK_STATUSES.INVOICED
    ? { ...additionalWork, status: ADDITIONAL_WORK_STATUSES.CAN_BE_INVOICED }
    : additionalWork;

/**
 * Free-form customer prefill for a draft created from a shareholder change.
 * Intentionally lighter than getCustomerChanges: there is no registry
 * customer id to link — the user picks one in the draft view before sending.
 */
export const getShareholderChangeCustomerDraftChanges = (source) => ({
  customerName: source.customerName || '',
  customerBusinessId: source.customerBusinessId || '',
  customerEmail: source.customerEmail || '',
  customerPhone: source.customerPhone || '',
  customerBillingEmail: source.customerEmail || '',
  customerBillingStreet1: source.customerStreet1 || '',
  customerBillingStreet2: source.customerStreet2 || '',
  customerBillingPostCode: source.customerPostCode || '',
  customerBillingCity: source.customerCity || '',
  billingType: 'email',
});
